import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  inject,
  linkedSignal,
  signal,
  viewChild
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { deepCopy, uuid, Uuid } from '@softline/core';
import { translator } from '@softline/ui-core';
import { DsTabsComponent, TabItem } from '@softline/ui-core/tabs';
import { PageFilterService } from '../../../services/page-filter.service';
import { PageFilterComponentBase } from '../page-filter-component-base';
import { ConditionFilter, FieldFilter, Filter } from '../../../../types/filter';
import { FilterConfig, FilterItemConfig } from '../../../../types/filter-config';
import { FilterOperator } from '../../../../types/filter-operators';
import { FilterInputBase } from '../../filter-inputs/filter-input.base';
import { toQueryFilter } from '../../../../helper/to-query-filter.function';
import { excludeFilterNode } from '../../../../helper/exclude-filter-node.function';
import { pruneIncompleteFilter } from '../../../../helper/prune-incomplete-filter.function';
import {
  addNodeToGroup,
  ensureRootGroup,
  removeNodeFromTree,
  replaceNodeInTree,
  simplifyFilterTree,
  toggleGroupOperator
} from '../../../../helper/filter-tree.functions';
import { getBuilderOperators, isValuelessOperator } from '../../../../helper/builder-operators.function';
import { FilterTreeEditor } from './filter-tree-editor';
import { FilterNodeComponent } from './filter-node/filter-node.component';

@Component({
  selector: 'soft-page-filter-custom',
  imports: [
    NgComponentOutlet,
    DsTabsComponent,
    FilterNodeComponent
  ],
  templateUrl: './page-filter-custom.component.html',
  styleUrl: './page-filter-custom.component.css',
  providers: [
    {provide: PageFilterComponentBase, useExisting: forwardRef(() => PageFilterCustomComponent)},
    {provide: FilterTreeEditor, useExisting: forwardRef(() => PageFilterCustomComponent)}
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageFilterCustomComponent extends PageFilterComponentBase implements FilterTreeEditor {
  private readonly t = translator();
  readonly service = inject(PageFilterService);

  /** Index into service.sources, which the tabs carry as their value - so an internal
   *  source dropping out of the list leaves every other tab pointing at its own state. */
  readonly sourceIndex = signal(
    Math.max(0, this.service.sources.findIndex((source) => !source.internal))
  );

  readonly sourceTabs = computed<TabItem<number>[]>(() =>
    this.service.sources
      .map((source, index) => ({
        title: source.title ? `${source.title()}` : source.state.config.key ?? `${index + 1}`,
        value: index,
        internal: source.internal === true,
      }))
      .filter((tab) => !tab.internal)
      .map(({ title, value }) => ({ title, value }))
  );

  // local working copies - edits stay in memory until apply writes them to the drafts
  readonly trees = linkedSignal(() =>
    this.service.states().map((state) => ensureRootGroup(deepCopy(state.state())))
  );

  readonly currentTree = computed<ConditionFilter<any> | null>(
    () => this.trees()[this.sourceIndex()] ?? null
  );
  private readonly currentState = computed(() => this.service.states()[this.sourceIndex()] ?? null);
  readonly config = computed<FilterConfig<any>>(
    () => this.currentState()?.config ?? { items: [], initialValue: null }
  );

  readonly editing = signal<(FieldFilter<any> & { operator: 'in' | 'notIn' }) | null>(null);
  private readonly outlet = viewChild(NgComponentOutlet);

  readonly editingConfig = computed(() => {
    const editing = this.editing();
    return editing ? this.getItemConfig(editing.field as string) : null;
  });

  readonly editingComponent = computed(() => {
    const editing = this.editing();
    const config = this.editingConfig();
    if (!editing || !config) return null;
    return config.inputTypeFactory(config.type, editing.operator);
  });

  readonly editingDataService = computed(
    () => this.service.sources[this.sourceIndex()]?.dataService ?? null
  );

  readonly editingItemFilter = computed(() => {
    const editing = this.editing();
    if (!editing) return null;
    const value = (editing as { value?: unknown }).value;
    return Array.isArray(value) && value.length > 0 ? editing : null;
  });

  readonly editingQueryFilter = computed(() => {
    const editing = this.editing();
    const state = this.currentState();
    if (!editing || !state) return null;
    const tree = simplifyFilterTree(
      pruneIncompleteFilter(excludeFilterNode(this.currentTree(), editing.uuid))
    );
    return tree ? toQueryFilter(tree, state.config) : null;
  });

  override title = computed(() => {
    const config = this.editingConfig();
    if (config) return config.title();
    return this.t('#CORE_FILTER.TABS.CUSTOM')();
  });
  override canBack = computed(() => this.editing() !== null);
  override canApply = computed(() => true);
  override tabsVisible = computed(() => this.editing() === null);

  override back = () => {
    this.editing.set(null);
  };

  override apply = () => {
    const editing = this.editing();
    if (editing) {
      this.applyEditedValues(editing);
      this.editing.set(null);
      return;
    }
    const states = this.service.states();
    this.trees().forEach((tree, index) => {
      const state = states[index];
      if (!state) return;
      state.set(simplifyFilterTree(pruneIncompleteFilter(tree)) as Filter<any>);
    });
    this.service.applyFilter();
    this.service.opened.set(false);
  };

  override reset = () => {
    this.editing.set(null);
    this.trees.set(this.service.states().map((state) => ensureRootGroup(deepCopy(state.state()))));
  };

  private applyEditedValues(editing: FieldFilter<any>): void {
    const input = (this.outlet()?.componentInstance as FilterInputBase<any, FilterOperator>) ?? null;
    const tree = this.currentTree();
    if (!input || !tree) return;
    const filter = input.getFilterValue();
    const replacement = filter
      ? ({ ...filter, uuid: editing.uuid } as Filter<any>)
      : ({ ...editing, value: [] } as Filter<any>);
    this.updateCurrentTree(replaceNodeInTree(tree, editing.uuid, replacement));
  }

  // FilterTreeEditor

  getItemConfig(field: string): FilterItemConfig<any> | null {
    return this.config().items.find((o) => o.field === field) ?? null;
  }

  addCondition(groupUuid: Uuid): void {
    const item = this.config().items.find((o) => getBuilderOperators(o).length > 0);
    if (!item) return;
    this.mutateTree((tree) =>
      addNodeToGroup(tree, groupUuid, this.createCondition(item, this.initialOperator(item)))
    );
  }

  addGroup(groupUuid: Uuid): void {
    this.mutateTree((tree) =>
      addNodeToGroup(tree, groupUuid, {
        uuid: uuid(),
        type: 'conditions',
        operator: 'AND',
        filters: [],
      })
    );
  }

  remove(nodeUuid: Uuid): void {
    this.mutateTree((tree) =>
      tree.uuid === nodeUuid ? tree : removeNodeFromTree(tree, nodeUuid)
    );
  }

  setGroupOperator(groupUuid: Uuid, operator: 'AND' | 'OR'): void {
    this.mutateTree((tree) => toggleGroupOperator(tree, groupUuid, operator));
  }

  setConditionField(nodeUuid: Uuid, field: string): void {
    const item = this.getItemConfig(field);
    if (!item) return;
    this.mutateTree((tree) =>
      replaceNodeInTree(tree, nodeUuid, this.createCondition(item, this.initialOperator(item), nodeUuid))
    );
  }

  setConditionOperator(nodeUuid: Uuid, operator: FilterOperator): void {
    const node = this.findNode(nodeUuid);
    if (!node || node.type === 'conditions') return;
    const item = this.getItemConfig(node.field as string);
    if (!item) return;
    this.mutateTree((tree) =>
      replaceNodeInTree(tree, nodeUuid, this.createCondition(item, operator, nodeUuid))
    );
  }

  setConditionValue(nodeUuid: Uuid, value: unknown): void {
    const node = this.findNode(nodeUuid);
    if (!node || node.type === 'conditions') return;
    this.mutateTree((tree) =>
      replaceNodeInTree(tree, nodeUuid, { ...node, value } as Filter<any>)
    );
  }

  editValues(node: FieldFilter<any>): void {
    const operator = node.operator === 'notIn' ? 'notIn' : 'in';
    this.editing.set({ ...node, operator } as FieldFilter<any> & { operator: 'in' | 'notIn' });
  }

  private initialOperator(item: FilterItemConfig<any>): FilterOperator {
    const operators = getBuilderOperators(item);
    return operators.includes(item.defaultOperator) ? item.defaultOperator : operators[0];
  }

  private createCondition(
    item: FilterItemConfig<any>,
    operator: FilterOperator,
    keepUuid?: Uuid
  ): Filter<any> {
    const node: Record<string, unknown> = {
      uuid: keepUuid ?? uuid(),
      type: item.type,
      field: item.field,
      operator,
    };
    if (!isValuelessOperator(operator)) {
      if (operator === 'in' || operator === 'notIn') node['value'] = [];
      else if (operator === 'between') node['value'] = { from: null, to: null };
      else node['value'] = null;
    }
    return node as unknown as Filter<any>;
  }

  private findNode(nodeUuid: Uuid): Filter<any> | null {
    const root = this.currentTree();
    if (!root) return null;
    const traverse: Filter<any>[] = [root];
    while (traverse.length > 0) {
      const current = traverse.shift();
      if (!current) break;
      if (current.uuid === nodeUuid) return current;
      if (current.type === 'conditions') traverse.push(...current.filters);
    }
    return null;
  }

  private mutateTree(fn: (tree: ConditionFilter<any>) => ConditionFilter<any>): void {
    const index = this.sourceIndex();
    this.trees.update((trees) => trees.map((tree, i) => (i === index ? fn(tree) : tree)));
  }

  private updateCurrentTree(tree: ConditionFilter<any>): void {
    this.mutateTree(() => tree);
  }
}
